import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from '../services/api';

export interface Objection {
    id: string;
    center_id: string;
    name: string;
    response: string;
    description?: string;
    icon?: string;
    order_index: number;
}

interface ObjectionStore {
    objections: Objection[];
    loading: boolean;
    fetchObjections: (centerId: string) => Promise<void>;
    addObjection: (centerId: string, objection: Partial<Objection>) => Promise<void>;
    updateObjection: (id: string, updates: Partial<Objection>) => Promise<void>;
    removeObjection: (id: string) => Promise<void>;
}

export const useObjectionStore = create<ObjectionStore>()(
    persist(
        (set) => ({
            objections: [],
            loading: false,
            fetchObjections: async (centerId) => {
                set({ loading: true });
                try {
                    const response = await api.get(`/objections/center/${centerId}`);
                    set({ objections: response.data || [] });
                } catch (error) {
                    console.error('Failed to fetch objections:', error);
                } finally {
                    set({ loading: false });
                }
            },
            addObjection: async (centerId, objection) => {
                try {
                    const response = await api.post('/objections', { ...objection, center_id: centerId });
                    set((state) => ({
                        objections: [...state.objections, response.data],
                    }));
                } catch (error) {
                    console.error('Failed to add objection:', error);
                }
            },
            updateObjection: async (id, updates) => {
                try {
                    const response = await api.put(`/objections/${id}`, updates);
                    set((state) => ({
                        objections: state.objections.map((obj) =>
                            obj.id === id ? { ...obj, ...response.data } : obj
                        ),
                    }));
                } catch (error) {
                    console.error('Failed to update objection:', error);
                }
            },
            removeObjection: async (id) => {
                try {
                    await api.delete(`/objections/${id}`);
                    set((state) => ({
                        objections: state.objections.filter((obj) => obj.id !== id),
                    }));
                } catch (error) {
                    console.error('Failed to remove objection:', error);
                }
            },
        }),
        {
            name: 'objection-storage',
        }
    )
);
